<template>
    <div class="content">
        <h3 class="mt-2 mb-5 ml-2 is-hidden-desktop"><i class="fal fa-address-book fa-fw mr-3"></i>Trip Contacts</h3>

        <!-- Free-form trip-level admin/office contacts, shown above the participant roster. -->
        <div class="white-container content ql-editor" v-if="trip.app_contacts" v-html="trip.app_contacts"></div>

        <!-- The trip's contact directory: external parties (restaurants, hotels,
             booking offices, guides, drivers) attached to the trip, its hotels,
             add-ons and itinerary events. Grouped by kind, each entry showing
             where on the trip it came from. -->
        <div class="mt-5" v-for="group in directoryGroups" :key="group.value">
            <h5 class="ml-2 mb-4"><i class="far fa-fw mr-2" :class="group.icon"></i>{{ group.label }}</h5>
            <div class="white-container">
                <div class="contact-card" v-for="entry in group.contacts" :key="entry.id">
                    <div class="contact-name">
                        {{ entry.primary }}
                        <span class="contact-role" v-if="entry.secondary"> &middot; {{ entry.secondary }}</span>
                    </div>
                    <div class="contact-line" v-if="entry.title">{{ entry.title }}</div>
                    <div class="contact-line" v-for="email in entry.emails" :key="'e' + email">
                        <i class="far fa-envelope fa-fw mr-2"></i><a :href="'mailto:' + email">{{ email }}</a>
                    </div>
                    <div class="contact-line" v-for="phone in entry.phones" :key="'p' + phone">
                        <i class="far fa-phone fa-fw mr-2"></i><a :href="'tel:' + telLink(phone)">{{ telLink(phone) }}</a>
                    </div>
                    <div class="contact-line" v-if="entry.website">
                        <i class="far fa-globe fa-fw mr-2"></i><a :href="entry.website" target="_blank" rel="noopener">{{ entry.website }}</a>
                    </div>
                    <div class="contact-line" v-if="entry.location"><i class="far fa-location-dot fa-fw mr-2"></i>{{ entry.location }}</div>
                    <div class="contact-line" v-if="entry.notes"><i class="far fa-sticky-note fa-fw mr-2"></i>{{ entry.notes }}</div>
                    <!-- Where on the trip this contact is used. One line per
                         attachment, so the same driver on five transfers reads
                         as one contact with five uses. -->
                    <div class="contact-source" v-for="use in entry.uses" :key="use.link_id">
                        <i class="far fa-link fa-fw mr-2"></i>{{ use.title }}
                        <span v-if="use.day_on"> &middot; {{ use.day_on | moment('ddd, MMM Do') }}</span>
                        <span v-if="use.note"> &middot; {{ use.note }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Auto-compiled full contact list pulled from every trip record (staff only).
             Unlike the Travelers roster, this ignores the travelers' profile share flags. -->
        <div class="mt-5" v-if="loading">
            <h5 class="has-text-grey"><i class="fal fa-tennis-ball fa-spin mr-3"></i>Gathering contacts from the trip records...</h5>
        </div>
        <div class="mt-5" v-else-if="contacts.length">
            <h5 class="ml-2 mb-4"><i class="far fa-address-book fa-fw mr-2"></i>Participants</h5>
            <div class="white-container">
                <div class="contact-card" v-for="c in contacts" :key="c.id">
                    <div class="contact-name">
                        {{ c.last_name }}, {{ c.first_name }}<template v-if="c.middle_name"> {{ c.middle_name }}</template><template v-if="c.preferred_name"> &ldquo;{{ c.preferred_name }}&rdquo;</template>
                        <span class="contact-role" v-if="c.role"> &middot; {{ c.role }}</span>
                    </div>
                    <div class="contact-line" v-if="c.email"><i class="far fa-envelope fa-fw mr-2"></i><a :href="'mailto:' + c.email">{{ c.email }}</a></div>
                    <div class="contact-line" v-if="c.phone"><i class="far fa-phone fa-fw mr-2"></i><a :href="'tel:' + telLink(c.phone)">{{ telLink(c.phone) }}</a></div>
                    <div class="contact-line" v-if="c.phone_2"><i class="far fa-phone fa-fw mr-2"></i><a :href="'tel:' + telLink(c.phone_2)">{{ telLink(c.phone_2) }}</a></div>
                    <div class="contact-line" v-if="fullAddress(c)"><i class="far fa-location-dot fa-fw mr-2"></i>{{ fullAddress(c) }}</div>
                    <div class="contact-line" v-if="c.instagram"><i class="fab fa-instagram fa-fw mr-2"></i>{{ c.instagram }}</div>
                    <div class="contact-line" v-if="c.facebook"><i class="fab fa-facebook-f fa-fw mr-2"></i>{{ c.facebook }}</div>
                    <div class="contact-line" v-if="c.twitter"><i class="fab fa-twitter fa-fw mr-2"></i>{{ c.twitter }}</div>
                    <div class="contact-line" v-if="c.whatsapp"><i class="fab fa-whatsapp fa-fw mr-2"></i>{{ c.whatsapp }}</div>
                </div>
            </div>
        </div>

        <!-- Nothing at all yet -->
        <div class="white-container content has-text-centered"
             v-if="!loading && !directoryLoading && !contacts.length && !directoryGroups.length && !trip.app_contacts">
            <h4 class="pt-6 pb-6 has-text-grey"><i class="fal fa-message-captions mr-2"></i> More information will be available later.  Check back soon!</h4>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import {client as http} from "../../http_client";

    // Mirrors server/src/components/contact_types.js (and Roger's
    // client/src/components/contact_meta.js). Order here is the order the
    // groups render in. Keep the three in sync.
    const TYPES = [
        {value: 'restaurant', label: 'Restaurants', icon: 'fa-utensils'},
        {value: 'hotel', label: 'Hotels', icon: 'fa-hotel'},
        {value: 'booking-office', label: 'Booking Offices', icon: 'fa-clipboard-check'},
        {value: 'vendor', label: 'Vendors', icon: 'fa-truck'},
        {value: 'guide', label: 'Guides', icon: 'fa-compass'},
        {value: 'driver', label: 'Drivers', icon: 'fa-taxi'},
        {value: 'tennis', label: 'Tennis & Tournament', icon: 'fa-tennis-ball'},
        {value: 'emergency', label: 'Emergency', icon: 'fa-triangle-exclamation'},
        {value: 'other', label: 'Other Contacts', icon: 'fa-address-card'}
    ];

    export default {
        data: function() {
            return {
                loading: false,
                directoryLoading: false,
                contacts: [],
                directory: []
            };
        },
        computed: {
            ...mapGetters(['trip', 'registration']),
            isStaff: function() {
                return this.registration.type === 'staff' || this.registration.second_type === 'staff';
            },
            /*
                The endpoint returns one row per ATTACHMENT so provenance
                survives; collapse to one entry per contact here, carrying the
                uses as sub-lines. A contact whose role differs from its type
                (the restaurant acting as its own booking office) is filed
                under the role, which is what staff are looking for.
            */
            directoryGroups: function() {
                const byType = {};

                this.directory.forEach(row => {
                    const type = row.role || row.type || 'other';
                    if (!byType[type]) byType[type] = {};

                    let entry = byType[type][row.id];
                    if (!entry) {
                        entry = byType[type][row.id] = {
                            id: row.id,
                            // Same display rule as Roger and the server.
                            primary: row.company || row.name || '',
                            secondary: row.company ? row.name : null,
                            title: row.title,
                            emails: [row.email, row.email_2].filter(Boolean),
                            phones: [row.phone, row.phone_2].filter(Boolean),
                            website: row.website,
                            location: [row.address, row.city, row.country].filter(Boolean).join(', '),
                            notes: row.directory_notes,
                            uses: []
                        };
                    }

                    if (row.source && row.source.title) {
                        entry.uses.push({
                            link_id: row.link_id,
                            title: row.source.title,
                            day_on: row.source.day_on,
                            note: row.label || row.link_notes || null
                        });
                    }
                });

                return TYPES
                    .map(t => ({
                        value: t.value,
                        label: t.label,
                        icon: t.icon,
                        contacts: Object.keys(byType[t.value] || {}).map(id => byType[t.value][id])
                    }))
                    .filter(g => g.contacts.length);
            }
        },
        methods: {
            // Phone numbers are now stored normalized with a leading "+". Collapse any
            // number of leading "+" into exactly one so we never render "++1650...".
            telLink: function(p) {
                if (!p) return '';
                return '+' + String(p).replace(/^\++/, '');
            },
            fullAddress: function(c) {
                const cityLine = [c.city, c.state].filter(Boolean).join(', ');
                return [c.address, c.address_2, [cityLine, c.zip].filter(Boolean).join(' '), c.country]
                    .filter(Boolean).join(', ').trim();
            },
            loadContacts: function() {
                if (!this.registration.id || !this.isStaff) return;
                this.loading = true;
                http.get('/app/trips/' + this.registration.trip_id + '/staff/contacts').then(response => {
                    this.contacts = response.data || [];
                    this.loading = false;
                }).catch(() => {
                    this.loading = false;
                });
            },
            loadDirectory: function() {
                if (!this.registration.id || !this.isStaff) return;
                this.directoryLoading = true;
                http.get('/app/trips/' + this.registration.trip_id + '/staff/directory').then(response => {
                    this.directory = response.data || [];
                    this.directoryLoading = false;
                }).catch(() => {
                    this.directoryLoading = false;
                });
            }
        },
        watch: {
            registration: function() {
                this.loadContacts();
                this.loadDirectory();
            }
        },
        mounted() {
            this.loadContacts();
            this.loadDirectory();
        }
    };
</script>

<style lang="scss" scoped>
    .contact-card {
        border-left: 3px solid #628b21;
        padding-left: 0.7rem;
        padding-bottom: 0.9rem;
        margin-bottom: 0.9rem;
        border-bottom: 1px solid #ededed;

        &:last-child {
            padding-bottom: 0;
            margin-bottom: 0;
            border-bottom: 0;
        }

        .contact-name {
            font-weight: 700;
            margin-bottom: 0.3rem;
        }

        .contact-role {
            font-weight: 400;
            color: #6b6b6b;
            font-size: 0.85rem;
            text-transform: capitalize;
        }

        .contact-line {
            font-size: 0.95rem;
            margin-bottom: 0.15rem;
        }

        // Where on the trip a directory contact is used. Quieter than the
        // contact's own details -- it's context, not the number you're after.
        .contact-source {
            font-size: 0.85rem;
            color: #6b6b6b;
            margin-top: 0.25rem;
        }
    }
</style>
